import { alpha, darken, Theme } from "@mui/material";

export const useStyles = (theme: Theme, isModeLight: boolean) => {
  const basePaperBg = isModeLight
    ? (theme.palette.background as any).paper
    : (theme.palette.background as any).paperLinkedMaps;

  const strokeScale = (scale: number, base: number, cap = 2.5) =>
    base * Math.min(scale, cap);

  const smoothTransition =
    "width 0.18s ease, height 0.18s ease, padding 0.18s ease, " +
    "border-radius 0.18s ease, box-shadow 0.2s ease, opacity 0.2s ease";

  const disasterSurface = (colors: any, tint: number) => ({
    backgroundColor: basePaperBg,
    backgroundImage: `linear-gradient(${alpha(
      colors.background,
      tint,
    )}, ${alpha(colors.background, tint)})`,
  });

  return {
    detailedPaper: (
      configElement: any,
      editMode: boolean | undefined,
      colors: any,
      scale = 1,
    ) => ({
      ...(configElement?.severity === 5 && !editMode
        ? disasterSurface(colors, 0.22)
        : { backgroundColor: basePaperBg }),
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      padding: `${6 * scale}px ${12 * scale}px`,
      gap: `${8 * scale}px`,
      borderRadius: `${8 * scale}px`,
      minWidth: `${180 * scale}px`,
      maxWidth: `${200 * scale}px`,
      transition: smoothTransition,
    }),
    iconBox: (
      configElement: any,
      editMode: boolean | undefined,
      colors: any,
      withBorderBottom = false,
      scale = 1,
    ) => ({
      display: "flex",
      position: "relative",
      alignItems: "center",
      justifyContent: "center",
      padding: `${6.4 * scale}px`,
      ...(configElement?.severity === 5 && !editMode
        ? disasterSurface(colors, 0.85)
        : {
            backgroundColor: isModeLight
              ? (theme.palette.background as any).paperLinkedMaps
              : theme.palette.background.paper,
          }),
      borderRadius: `${6 * scale}px`,
      borderBottom: withBorderBottom
        ? `${strokeScale(scale, 3)}px solid ${
            colors.border || theme.palette.secondary.main
          }`
        : undefined,
      transition: smoothTransition,
    }),
    simplePaper: (
      configElement: any,
      editMode: boolean | undefined,
      colors: any,
      scale = 1,
    ) => ({
      ...(configElement?.severity === 5 && !editMode
        ? disasterSurface(colors, 0.22)
        : { backgroundColor: basePaperBg }),
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      padding: `${4 * scale}px`,
      gap: `${8 * scale}px`,
      borderRadius: `${8 * scale}px`,
      minWidth: `${120 * scale}px`,
      maxWidth: `${135 * scale}px`,
      transition: smoothTransition,
    }),
    compactPaper: (
      configElement: any,
      editMode: boolean | undefined,
      colors: any,
      scale = 1,
    ) => {
      const isDown = configElement?.isDown && !editMode;
      const downColor = theme.palette.color.zabbixSeverty.disaster;
      return {
        ...(configElement?.severity === 5 && !editMode
          ? { backgroundColor: colors.background }
          : { backgroundColor: basePaperBg }),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        minWidth: `${50 * scale}px`,
        minHeight: `${50 * scale}px`,
        width: "100%",
        height: "100%",
        border: `${strokeScale(scale, 2)}px solid ${
          colors.border || theme.palette.secondary.main
        }`,
        transition: smoothTransition,
        ...(isDown
          ? {
              animation: "compact-down 0.7s ease-in-out infinite alternate",
              "@keyframes compact-down": {
                "0%": {
                  borderColor: alpha(downColor, 0.25),
                },
                "100%": {
                  borderColor: downColor,
                },
              },
            }
          : {}),
      };
    },
    bow: (
      colorsOpacity: string,
      isCompact: boolean,
      isSimple: boolean,
      pulse = false,
      scale = 1,
    ) => {
      const colorHash = colorsOpacity.replace(/[^a-zA-Z0-9]/g, "");
      const animationName = `selected-alert-pulse-${colorHash}`;
      const appearName = `selected-appear-${colorHash}`;

      const gap = (isCompact ? 8 : 6) * scale;
      const stroke = strokeScale(scale, 2);
      const ring = 4 * scale;
      const glowBlur = 16 * scale;
      const glowSpread = 3 * scale;

      return {
        position: "absolute",
        border: `${stroke}px solid ${alpha(colorsOpacity, 0.95)}`,
        boxShadow: `0 0 0 ${ring}px ${alpha(
          colorsOpacity,
          0.18,
        )}, 0 0 ${glowBlur}px ${glowSpread}px ${alpha(colorsOpacity, 0.55)}`,
        left: `-${gap}px`,
        top: `-${gap}px`,
        borderRadius: isCompact ? "50%" : `${(isSimple ? 8 : 8) * scale}px`,
        width: `calc(100% + ${gap * 2}px)`,
        height: `calc(100% + ${gap * 2}px)`,
        pointerEvents: "none",
        zIndex: -1,
        transition:
          "opacity 0.2s ease, box-shadow 0.2s ease, left 0.18s ease, " +
          "top 0.18s ease, width 0.18s ease, height 0.18s ease",
        animation: pulse
          ? `${appearName} 0.2s ease-out, ${animationName} 1.4s ease-in-out infinite alternate`
          : `${appearName} 0.2s ease-out`,
        [`@keyframes ${appearName}`]: {
          "0%": { opacity: 0, transform: "scale(0.92)" },
          "100%": { opacity: 1, transform: "scale(1)" },
        },
        ...(pulse
          ? {
              [`@keyframes ${animationName}`]: {
                "0%": {
                  boxShadow: `0 0 0 ${ring * 0.75}px ${alpha(
                    colorsOpacity,
                    0.15,
                  )}, 0 0 ${glowBlur * 0.625}px ${glowSpread * 0.67}px ${alpha(
                    colorsOpacity,
                    0.4,
                  )}`,
                },
                "100%": {
                  boxShadow: `0 0 0 ${ring * 1.25}px ${alpha(
                    colorsOpacity,
                    0.3,
                  )}, 0 0 ${glowBlur * 1.5}px ${glowSpread * 2.3}px ${alpha(
                    colorsOpacity,
                    0.72,
                  )}`,
                },
              },
            }
          : {}),
      };
    },
    handle: (editMode: boolean | undefined, scale = 1) => {
      const size = 8 * scale;
      return {
        width: `${size}px`,
        height: `${size}px`,
        minWidth: `${size}px`,
        minHeight: `${size}px`,
        borderRadius: "50%",
        background: editMode ? "#b1b1b7" : "transparent",
        border: editMode
          ? `${strokeScale(scale, 1, 3)}px solid ${
              theme.palette.background.default
            }`
          : "transparent",
        transition: "width 0.18s ease, height 0.18s ease",
      };
    },
    alertHalo: (
      colors: any,
      viewTypeEl: string,
      selected: boolean | undefined,
      scale = 1,
    ) => {
      const stroke = strokeScale(scale, 2);
      const off = 2 * scale;
      return {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        borderRadius: viewTypeEl === "compact" ? "50%" : `${8 * scale}px`,
        "--alert-color": colors.border,
        ...(viewTypeEl === "compact"
          ? {
              "&::before, &::after": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: "inherit",
                border: selected
                  ? "none"
                  : `${stroke}px solid var(--alert-color)`,
                animation:
                  "alert-pulse-circle 2s cubic-bezier(0.165, 0.84, 0.44, 1) infinite",
              },
              "&::after": {
                animationDelay: "1s",
              },
              "@keyframes alert-pulse-circle": {
                "0%": { transform: "scale(1)", opacity: 0.5 },
                "100%": { transform: "scale(1.6)", opacity: 0 },
              },
            }
          : {
              "&::before": {
                content: '""',
                position: "absolute",
                top: `-${off}px`,
                left: `-${off}px`,
                right: `-${off}px`,
                bottom: `-${off}px`,
                borderRadius: `${8 * scale + off}px`,
                border: selected
                  ? "none"
                  : `${stroke}px solid var(--alert-color)`,
                animation:
                  "alert-glow-rect 1.2s ease-in-out infinite alternate",
              },
              "@keyframes alert-glow-rect": {
                "0%": {
                  boxShadow: `0 0 ${4 * scale}px ${1 * scale}px var(--alert-color)`,
                  opacity: 0.4,
                },
                "100%": {
                  boxShadow: `0 0 ${20 * scale}px ${6 * scale}px var(--alert-color)`,
                  opacity: 0.7,
                },
              },
            }),
      };
    },
    badge: (configElement: any, editMode: boolean, scale = 1) => {
      const isDown = configElement?.isDown && !editMode;
      const downColor = theme.palette.color.zabbixSeverty.disaster;
      return {
        position: "absolute",
        bottom: -2 * scale,
        right: -3 * scale,
        width: 13 * scale,
        height: 13 * scale,
        borderRadius: "50%",
        backgroundColor: isDown ? downColor : theme.palette.success.main,
        border: `${strokeScale(scale, 2)}px solid ${
          (theme.palette.background as any).paperLinkedMaps
        }`,
        ...(isDown
          ? {
              animation: "badge-down 0.7s ease-in-out infinite alternate",
              "@keyframes badge-down": {
                "0%": {
                  backgroundColor: alpha(downColor, 0.45),
                },
                "100%": {
                  backgroundColor: downColor,
                },
              },
            }
          : {}),
      };
    },
    titleText: (scale = 1) => ({
      color: theme.palette.text.primary,
      fontSize: `${0.8125 * scale}rem`,
      fontWeight: 600,
      lineHeight: 1.2,
    }),
    subTitleText: (
      configElement: any,
      editMode: boolean | undefined,
      scale = 1,
    ) => ({
      color:
        isModeLight && configElement?.severity === 5 && !editMode
          ? darken(theme.palette.text.disabled, 0.5)
          : isModeLight
            ? darken(theme.palette.text.disabled, 0.3)
            : theme.palette.text.disabled,
      fontSize: `${0.6875 * scale}rem`,
      lineHeight: 1.2,
    }),
  };
};
